import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useFavorites } from '@/arche/arches/favorites/hooks/useFavorites';

/** Sentinel pulldown values distinct from any arche id. */
export const ARCHE_AUTO = '__auto__';
export const ARCHE_SYSTEM = '__system__';

/**
 * Convert a picker selection into the create-submission arche fields.
 * - Auto-detect → archeId null, no source (the async pipeline infers it later).
 * - Fias system → archeId null, source 'user' (locks out AI; user said platform).
 * - A specific arche → that id. Source is 'route' when the value is exactly the
 *   one prefilled from the launch context (`?arche=`) and the user left it as-is,
 *   else 'user' (an explicit choice). Both lock out AI inference.
 *
 * @param routePrefillArcheId the arche id that was prefilled from the URL (the
 *   raw `?arche=` value), or null when the form wasn't launched from an arche.
 */
export function archePickerToTag(
  value: string,
  routePrefillArcheId?: string | null,
): {
  archeId: string | null;
  archeTagSource?: 'route' | 'user';
} {
  if (value === ARCHE_AUTO) return { archeId: null };
  if (value === ARCHE_SYSTEM) return { archeId: null, archeTagSource: 'user' };
  const source = routePrefillArcheId && value === routePrefillArcheId ? 'route' : 'user';
  return { archeId: value, archeTagSource: source };
}

interface ArchePickerProps {
  value: string;
  onChange: (value: string) => void;
  /** Optional label text; omit to render just the select. */
  label?: string;
}

/**
 * Lets a user say which arche their report concerns. Defaults to "Auto-detect"
 * so an untouched submission is left for the AI to tag; the user can instead
 * pick "Fias system" or one of the arches on their worktop.
 */
export function ArchePicker({ value, onChange, label = 'What is this about?' }: ArchePickerProps) {
  const { favorites } = useFavorites();

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ARCHE_AUTO}>Auto-detect (let support figure it out)</SelectItem>
          <SelectItem value={ARCHE_SYSTEM}>Fias system (not arche-specific)</SelectItem>
          {favorites.map((f) => (
            <SelectItem key={f.archId} value={f.archId}>
              {f.archeName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
